import { motion, AnimatePresence } from 'motion/react';
import { useState } from 'react';
import type { Persona } from '../types';
import { avatarConfigMap } from '../config/personas';
import { createGlassEffect, glassEffect } from '@ideoz/ui/utils';

interface AvatarLabelProps {
  selected: Persona;
}

export function AvatarLabel({ selected }: AvatarLabelProps): JSX.Element {
  const config = avatarConfigMap[selected];
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <motion.div
      className="absolute content-stretch flex items-center p-5 gap-6 rounded-full"
      initial={false}
      animate={{
        left: config.left,
        top: config.top,
        width: config.width,
      }}
      transition={{ duration: 0.7, ease: 'easeInOut' }}
      style={createGlassEffect(0.7)}
    >
      <motion.div transition={{ duration: 0.6, ease: 'easeInOut' }}>
        <AnimatePresence mode="wait">
          <div key={selected} className="relative w-[6rem] h-[6rem]">
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-200 rounded-full animate-pulse" />
            )}
            <motion.img
              key={selected}
              alt="avatar image"
              className="w-[6rem] h-[6rem] rounded-full object-cover"
              src={`${config.image}?w=160&h=160&fit=crop`}
              loading="lazy"
              onLoad={() => setImageLoaded(true)}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: imageLoaded ? 1 : 0, scale: 0.8 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </AnimatePresence>
      </motion.div>
      <div className="content-stretch flex flex-col items-start not-italic relative shrink-0 text-nowrap whitespace-pre">
        <AnimatePresence mode="wait">
          <motion.p
            key={`title-${selected}`}
            className="font-semibold text-3xl relative shrink-0 text-[#181d27]"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.3 }}
          >
            {selected.charAt(0).toUpperCase() + selected.slice(1)}
          </motion.p>
        </AnimatePresence>
        <motion.p
          key={`name-${selected}`}
          className="font-normal text-lg relative shrink-0 text-[#535862]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1 }}
        >
          {config.name}
        </motion.p>
      </div>
    </motion.div>
  );
}
